import { cloneDeep, defaultsDeep } from 'lodash'
import {
  countInvoiceRowsProducts,
  createInvoiceRows,
  deleteInvoiceRow,
  updateInvoiceRowAmounts,
} from './invoice-rows.lib'
import { updateInvoiceHeaderAmounts } from './invoice-header.lib'
import { DeepPartial } from '../../../models/util.model'
import { ProductSelection } from '../../products'
import { Invoice, InvoiceRow } from '../models/invoice.entity'

/**
 * Invoice initial state
 */
const INVOICE_INITIAL_STATE: DeepPartial<Invoice> = {
  documentType: 'INVOICE',
  productsCount: 0,
  rowsCount: 0,
  packagesCount: 0,
  header: {
    date: new Date(),
    rifDate: new Date(),
    currency: 'EUR',
    shippingTaxes: 0,
    bodyTaxes: 0,
    subTotal: 0,
    subTotalWithTaxes: 0,
    totalInvoiceAmount: 0,
  },
  rows: [],
}

/**
 * Init invoice data
 * @param invoice - the partial invoice to init
 * @returns the invoice initialized
 */
export function initInvoice(invoice: Partial<Invoice> = {}): Invoice {
  return defaultsDeep(cloneDeep(invoice), INVOICE_INITIAL_STATE)
}

/**
 * Update invoice amounts
 * @param invoice - the invoice
 * @param priceWithTaxes - the price taxes flag
 * @returns the invoice updated
 */
export function updateInvoiceAmounts(
  invoice: Invoice,
  priceWithTaxes = false,
): Invoice {
  const rows = invoice.rows.map((row) =>
    updateInvoiceRowAmounts(row, priceWithTaxes),
  )

  const header = updateInvoiceHeaderAmounts(
    invoice.header,
    rows,
    priceWithTaxes,
  )

  return {
    ...invoice,
    header,
    rows,
    productsCount: countInvoiceRowsProducts(rows),
    rowsCount: rows.length,
  }
}

/**
 * Add products to an invoice
 * @param invoice - the invoice
 * @param selection - the products selection
 * @param priceTaxes - the price taxes flag
 * @returns the invoice updated
 */
export function addInvoiceProducts(
  invoice: Invoice,
  selection: ProductSelection[],
  priceTaxes = false,
): Invoice {
  return updateInvoiceAmounts(
    {
      ...invoice,
      rows: [...invoice.rows, ...createInvoiceRows(selection)],
    },
    priceTaxes,
  )
}

/**
 * Remove a row from an invoice
 * @param invoice - the invoice to update
 * @param row - the row to remove
 * @param priceTaxes - the price taxes flag
 * @returns the invoice updated
 */
export function removeInvoiceRow(
  invoice: Invoice,
  row: InvoiceRow,
  priceTaxes = false,
): Invoice {
  return updateInvoiceAmounts(
    {
      ...invoice,
      rows: deleteInvoiceRow(invoice.rows, row),
    },
    priceTaxes,
  )
}
